import React from 'react';
import { LockIcon } from 'lucide-react';
import { getUniversityBySlug } from '../../data/universityData';

// Shared Crestmont University brand chrome. The palette is fixed by the
// brand guide: deep navy as the primary surface, near-black ink for the
// utility strip and footer, and gold reserved for calls to action only.
export const CRESTMONT = {
  navy: '#00426b',
  ink: '#0b2338',
  gold: '#fcc23f',
  goldHover: '#e9ae26',
  mist: '#f1f3f5',
  paper: '#f7f9fb'
} as const;

// Reversed logo lockup for the dark footer — the official Crestmont logo is
// dark artwork and disappears on the ink surface, so the footer uses a
// light-on-dark mark instead.
function CrestmontLogoReversed() {
  return <a href="/universities/crestmont-university" className="flex items-center gap-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#fcc23f] rounded" aria-label="Crestmont University home" data-id="element-229" data-mp="15rozmckrvjb2">
      <svg viewBox="0 0 32 32" className="w-8 h-8 flex-shrink-0" aria-hidden="true" data-id="element-230" data-mp="1fq4m228uu4e8">
        <circle cx="16" cy="16" r="16" fill="#ffffff" fillOpacity="0.08" data-id="element-231" data-mp="x8kbe4qa91nm" />
        <path d="M16 7l7.5 13H8.5L16 7z" fill={CRESTMONT.gold} data-id="element-232" data-mp="sa0n3xo7vds3" />
        <path d="M16 14.5l4 5.5h-8l4-5.5z" fill={CRESTMONT.navy} data-id="element-233" data-mp="o9gqqwbgwe70" />
      </svg>
      <span className="leading-none" data-id="element-234" data-mp="javraz41hyjm">
        <span className="block text-[13px] font-bold tracking-wide text-white" data-id="element-235" data-mp="jbxchwers1f2">
          Crestmont
        </span>
        <span className="block text-[10px] font-semibold tracking-[0.18em] text-white/70 uppercase" data-id="element-236" data-mp="1g0mvxr61p235">
          University
        </span>
      </span>
    </a>;
}
interface CrestmontHeaderProps {
  /** Retained for callers; the restored nav has no in-nav CTA. */
  onApply?: () => void;
}

// The original Crestmont nav bar: white surface with the official logo
// centered and a "Secure" mark on the right — matching the other partner
// pages so the lead experience stays consistent.
export function CrestmontHeader(_props: CrestmontHeaderProps) {
  const university = getUniversityBySlug('crestmont-university');
  return <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-237" data-mp="2dwqr057fc5te">
      <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-238" data-mp="2it8xpambee8d">
        <a href="/universities/crestmont-university" className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b] rounded" aria-label="Crestmont University home" data-id="element-239" data-mp="251kjn1si9for">
          {university?.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-240" data-mp="202zjt3h60y0u" /> : <span className="text-xl font-bold" style={{
          color: CRESTMONT.navy
        }} data-id="element-241" data-mp="1q4jqquykveop">
              Crestmont University
            </span>}
        </a>
        <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-242" data-mp="3kjmcchkiacmc">
          <LockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-243" data-mp="18uzfdkpy7gve" />
          Secure
        </div>
      </div>
    </header>;
}
const FOOTER_COLUMNS: {
  heading: string;
  links: string[];
}[] = [{
  heading: 'Contact Admissions',
  links: ['1-800-555-0142', 'admissions@crestmont.edu', 'Mon–Fri, 8am–7pm ET']
}, {
  heading: 'Programs',
  links: ['Business Administration', 'Information Technology', 'Healthcare Administration', 'Project Management']
}, {
  heading: 'Admissions',
  links: ['How to Apply', 'Transfer Credit', 'Tuition & Aid', 'Military Benefits']
}, {
  heading: 'About Crestmont',
  links: ['Accreditation', 'Faculty', 'Student Outcomes', 'Consumer Information']
}];
const LEGAL_LINKS: {
  label: string;
  href: string;
}[] = [{
  label: 'Privacy Policy',
  href: '/privacy-policy'
}, {
  label: 'Cookie Policy',
  href: '/cookie-policy'
}, {
  label: 'Terms & Conditions',
  href: '/terms'
}, {
  label: 'Do Not Sell My Info',
  href: '/ca-notice'
}];
export function CrestmontFooter() {
  return <footer style={{
    backgroundColor: CRESTMONT.ink
  }} className="text-white" data-id="element-244" data-mp="8pf5egtu5h9j">
      <div className="max-w-6xl mx-auto px-4 py-14" data-id="element-245" data-mp="dngpn52zzmml">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5" data-id="element-246" data-mp="im09mzy16ocb">
          <div className="lg:col-span-1" data-id="element-247" data-mp="3h3dzte15sukx">
            <CrestmontLogoReversed data-id="element-248" data-mp="3h4fa3gacpd1l" />
            <p className="mt-4 text-xs leading-relaxed text-white/55" data-id="element-249" data-mp="3h1b0g3e49p3j">
              Education tailored to your life.
            </p>
            <ul className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2" aria-label="Crestmont University social media" data-id="element-250" data-mp="3r11u62uus02k">
              {['Facebook', 'Instagram', 'LinkedIn', 'YouTube'].map(network => <li key={network} className="text-[11px] font-semibold text-white/45" data-id="element-251" data-mp="2yb1xusef3vbb">
                    {network}
                  </li>)}
            </ul>
          </div>
          {FOOTER_COLUMNS.map(column => <div key={column.heading} data-id="element-252" data-mp="2luadl38c37bm">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#fcc23f]" data-id="element-253" data-mp="2lt92wzv8cmuf">
                {column.heading}
              </h2>
              <ul className="mt-4 space-y-2.5" data-id="element-254" data-mp="2gxjx4esma9po">
                {column.links.map(link => <li key={link} className="text-xs leading-relaxed text-white/65" data-id="element-255" data-mp="2cvf7dr5ahx8b">
                    {link}
                  </li>)}
              </ul>
            </div>)}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-256" data-mp="1y69bod5nup56">
          <p className="text-[11px] text-white/45" data-id="element-257" data-mp="1t7ftw8jccbz7">
            © {new Date().getFullYear()} Crestmont University. All rights
            reserved.
          </p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-258" data-mp="1ei4nm9a0vnbn">
            {LEGAL_LINKS.map(link => <li key={link.href} data-id="element-259" data-mp="1jjamkoua06tm">
                <a href={link.href} className="text-[11px] text-white/45 hover:text-white transition-colors" data-id="element-260" data-mp="15ihndqt14nk6">
                  {link.label}
                </a>
              </li>)}
          </ul>
        </div>

        <p className="mt-6 text-[10px] leading-relaxed text-white/35" data-id="element-261" data-mp="1y8zynf3wpv8o">
          This page is an advertisement. Crestmont University is a paid partner
          of FindOnlineDegrees.org, which is compensated when you request
          information. Program length, tuition, and outcomes shown are estimates
          for planning purposes and are not a guarantee of cost, completion
          time, employment, or salary. This page uses cookies and pixels from
          third parties, including Meta, for advertising and measurement — see
          the Cookie Policy for details and opt-out options.
        </p>
      </div>
    </footer>;
}